import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import spinnerImage from '../../../../../assets/images/dq-spinner.png';

export const AssessmentResultContainer = styled(Box)`
  padding: 30px 20px 0;
  width: 100%;
  text-align: center;
`;

export const ResultContent = styled(Box)`
  text-align: center;
`;

export const ResultHeading = styled(Box)`
  font-weight: bold;
  margin-top: 4vh;
  font-size: 2em;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-drag: none;
  pointer-events: auto;

  h1 {
    font-size: 2em;
    margin-top: 4vh;
    color: #ED6628;
    font-family: "Source Sans Pro", "Helvetica Neue", sans-serif;

    @media (max-width: 768px) {
      font-size: 1.5em;
    }
  }
`;

export const ResultBody = styled(Box)`
  font-weight: 400;
`;

export const ScoreContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  margin-left: auto;
  margin-right: auto;
  margin-top: 4vh;
  width: 100%;

  @media (max-width: 768px) {
    display: block;
    margin-top: 0;
    width: 100%;
  }

  @media (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
  }
`;

export const ScoreContent = styled(Box)`
  padding: 0 6vh;

  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
  }
`;

export const ScoreSpinner = styled(Box)`
  background: url(${spinnerImage}) no-repeat center center;
  background-size: contain;
  padding: 10%;
  width: 15vw;
  height: 15vw;
  max-width: 250px;
  max-height: 250px;
  font-family: "Source Sans Pro", "Helvetica Neue", sans-serif;

  /* Responsive font size - scales with container */
  font-size: clamp(1.8em, 6vw, 3.2em);

  @media (max-width: 1024px) {
    width: 30vw;
    height: 30vw;
    font-size: clamp(1.6em, 5.5vw, 2.8em);
  }

  @media (max-width: 768px) {
    width: 35vw;
    height: 35vw;
    font-size: clamp(1.4em, 5vw, 2.2em);
  }

  @media (max-width: 480px) {
    width: 45vw;
    height: 45vw;
    font-size: clamp(1.2em, 4vw, 1.8em);
  }

  /* Ensure the spinner maintains aspect ratio */
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

export const ScoreLabelPt = styled('span')`
  margin-top: -5px;
`;

export const ScoreDesc = styled(Box)`
  font-size: 1.3em;
  font-weight: bold;
  padding: 0.5em 1em;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-drag: none;
  pointer-events: auto;

  @media (max-width: 768px) {
    font-size: 1.1em;
    margin-bottom: 1vh;
  }
`;

// Box around the rubric table: sits beside the score dial and takes the width that is
// left (up to max-width); ScoreContainer wraps it below the dial when the pane is narrow.
export const RubricContainer = styled(Box)`
  background-color: var(--brand-bg, #f4f2f3);
  border-radius: 15px;
  padding: 10px 18px;
  box-sizing: border-box;
  flex: 1 1 420px;
  min-width: 0;
  max-width: 640px;
  align-self: flex-start;

  @media (max-width: 1024px) {
    flex: 0 0 auto;
    align-self: center;
    width: 100%;
  }

  @media (max-width: 768px) {
    padding: 8px 10px;
  }
`;

export const RubricTable = styled('table')`
  width: 100%;
  /* Room for the 0 of the scale, which hangs half a number below the last row */
  margin-bottom: 0.65rem;
  border-collapse: collapse;
  text-align: left;
  color: var(--brand-text, #000000);
  font-family: "Source Sans Pro", "Helvetica Neue", sans-serif;
  font-size: 1rem;
  line-height: 1.4;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-drag: none;
  pointer-events: auto;

  th,
  td {
    padding: 8px 10px;
    text-align: left;
    vertical-align: top;
  }

  th,
  td[data-level] {
    font-weight: bold;
  }

  td {
    border-top: 1px solid var(--brand-border, #D1D5DB);
  }

  td[data-level] {
    /* Band strip between the scale and the label, in the colour of the row */
    border-left: 12px solid currentColor;
    white-space: nowrap;
  }

  /* Same width without colour, so the Level heading starts where the labels below it start */
  th:first-of-type {
    border-left: 12px solid transparent;
  }

  /* Scale column: a number sits on a row edge, so it follows the row whatever its height */
  td[data-scale] {
    position: relative;
    width: 50px;
    padding: 0;
    border-top: 0;
    font-family: 'Lemon/Milk';
    font-size: 1.3rem;
    font-weight: bold;
    white-space: nowrap;
  }

  td[data-scale] span {
    position: absolute;
    top: 0;
    right: 10px;
    line-height: 1;
    transform: translateY(-50%);
  }

  td[data-scale] span[data-scale-floor] {
    top: auto;
    bottom: 0;
    color: var(--brand-text, #000000);
    transform: translateY(50%);
  }

  /* Level label and scale colours: the palette of the legend this table replaced */
  td[data-level="4"], td[data-scale="4"] { color: #3cc6f3; }
  td[data-level="3"], td[data-scale="3"] { color: #f5a335; }
  td[data-level="2"], td[data-scale="2"] { color: #838383; }
  td[data-level="1"], td[data-scale="1"] { color: #ff00c6; }

  @media (max-width: 768px) {
    font-size: 0.9rem;

    th,
    td {
      padding: 6px 8px;
    }

    td[data-scale] {
      font-size: 1.1rem;
    }
  }
`;
